import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  TableContainer,
} from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import DSNSummarySkeleton from './DsnSummarySkeleton';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';
import { dsnPageTestId } from '../test-ids';

type ObjectiveNote = {
  code: string;
  duration: string;
  note: string;
};

type Section = {
  date: string;
  therapist: string;
  notes: ObjectiveNote[];
  missed?: boolean;
};

type DSNSummaryModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  data: Section[];
};

const DSNSummaryModal: React.FC<DSNSummaryModalProps> = ({
  open,
  onClose,
  title = 'DSN Summary',
  data,
}) => {
  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ p: 2.5 }}>
        <Typography variant="h5">{title}</Typography>
      </DialogTitle>

      {dsnSummary?.loading ? (
        <DSNSummarySkeleton />
      ) : (
        <>
          <DialogContent>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Code | Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Objective Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.length ? (
                    data?.map((section: any, index) => (
                      <React.Fragment key={index}>
                        <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                          <TableCell
                            sx={{ border: '1px solid #ddd' }}
                            colSpan={2}
                          >
                            <Typography variant="h6">
                              {moment(section?.notesDate).format('MMMM D')} | {section?.name}
                              {section?.jsonData?.generalInformation?.missedVisit == 'Yes'
                                ? ' (Missed visit)'
                                : null}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {section?.jsonData?.billing?.codes?.map((note: any, idx: any) => (
                          <TableRow key={idx}>
                            <TableCell
                              sx={{ width: '16%', verticalAlign: 'top', border: '1px solid #ddd' }}
                            >
                              <Typography variant="subtitle2">{note?.code}</Typography>
                              <Typography
                                sx={{ color: '#6C737F' }}
                                variant="caption"
                              >
                                {note?.minutes ? `${note?.minutes} Min.` : null}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', border: '1px solid #ddd' }}>
                              {note?.notes}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box
                          textAlign="center"
                          p={3}
                        >
                          <img
                            src={emptyImg}
                            alt="loader"
                            style={{
                              width: 620,
                              height: 240,
                              margin: 'auto',
                              alignItems: 'center',
                              alignContent: 'center',
                              marginTop: 50,
                              display: 'block',
                            }}
                          />
                          <Typography
                            color={'text.secondary'}
                            variant="h6"
                            fontWeight={'200'}
                          >
                            No results found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <Box
            display="flex"
            justifyContent="flex-end"
            p={2}
          >
            <Button
              data-testid={dsnPageTestId('dsn-summary-close')}
              variant="contained"
              onClick={onClose}
            >
              Done
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default DSNSummaryModal;
